const byName = (a, b) =>
  String(a.name).localeCompare(String(b.name), "en", { sensitivity: "base" });
const rating = (player) => Number(player.rating_used ?? player.elo ?? 0);

export function initialOlympiadRanking(teams) {
  return [...teams]
    .map((team) => {
      const ratings = [...(team.roster || [])]
        .map(rating)
        .sort((a, b) => b - a);
      return {
        ...team,
        seed_average:
          ratings.slice(0, 4).reduce((sum, value) => sum + value, 0) /
          Math.max(1, Math.min(4, ratings.length)),
        fifth_rating: ratings[4] || 0,
      };
    })
    .sort((a, b) => {
      const aStart = Number(a.start_number);
      const bStart = Number(b.start_number);
      const aHasStart = Number.isInteger(aStart) && aStart > 0;
      const bHasStart = Number.isInteger(bStart) && bStart > 0;
      if (aHasStart && bHasStart && aStart !== bStart) return aStart - bStart;
      if (aHasStart !== bHasStart) return aHasStart ? -1 : 1;
      return (
        b.seed_average - a.seed_average ||
        b.fifth_rating - a.fifth_rating ||
        byName(a, b)
      );
    })
    .map((team, index) => ({ ...team, initial_seed: index + 1 }));
}

const opponents = (team) => new Set(team.opponents || []);
const colourCost = (team, colour) => {
  const history = team.colours || [];
  const next = colour === "white" ? 1 : -1;
  const balance =
    history.reduce(
      (sum, item) => sum + (item === "white" ? 1 : item === "black" ? -1 : 0),
      0
    ) + next;
  const triple =
    history.length >= 2 &&
    history.at(-1) === colour &&
    history.at(-2) === colour;
  return Math.abs(balance) > 2
    ? 1000
    : Math.abs(balance) * 2 +
        (triple ? 100 : 0) +
        (history.at(-1) === colour ? 1 : 0);
};

function coloursFor(a, b, round, boardIndex) {
  if (round === 1)
    return (a.initial_seed + boardIndex) % 2 === 1
      ? { teamAColour: "white", teamBColour: "black" }
      : { teamAColour: "black", teamBColour: "white" };
  const aWhite = colourCost(a, "white") + colourCost(b, "black");
  const bWhite = colourCost(a, "black") + colourCost(b, "white");
  return aWhite <= bWhite
    ? { teamAColour: "white", teamBColour: "black" }
    : { teamAColour: "black", teamBColour: "white" };
}

function findCompletePairing(teams) {
  const legal = (a, b) => a.id !== b.id && !opponents(a).has(b.id) && !opponents(b).has(a.id);
  // Swiss score brackets: prefer upper half against lower half, not neighbours
  // in rating order. Otherwise strong teams eliminate one another repeatedly.
  const preferred = new Map();
  let carry = [];
  const groups = [...new Set(teams.map(t => Number(t.matchPoints || 0)))];
  for (const points of groups) {
    const group = [...carry, ...teams.filter(t => Number(t.matchPoints || 0) === points)];
    carry = group.length % 2 ? [group.pop()] : [];
    const half = group.length / 2;
    for (let i = 0; i < half; i++) {
      preferred.set(group[i].id, group[i + half].id);
      preferred.set(group[i + half].id, group[i].id);
    }
  }
  const cost = (a,b) => Math.abs(Number(a.matchPoints || 0) - Number(b.matchPoints || 0)) * 10000
    + (preferred.get(a.id) === b.id ? 0 : 1000)
    + Math.abs(Number(a.initial_seed || 0) - Number(b.initial_seed || 0));

  // Complete deterministic backtracking. Selecting the next team in stable
  // Swiss order and trying every legal alternative provides deterministic
  // swaps and backtracking. Candidate ordering makes equal-score pairings the
  // preference while adjacent groups float naturally.
  const search = (remaining, pairs) => {
    if (!remaining.length) return pairs;
    const first = remaining[0];
    const rest = remaining.slice(1);
    const candidates = rest
      .filter((candidate) => legal(first, candidate))
      .sort(
        (a, b) =>
          cost(first, a) - cost(first, b) ||
          a.initial_seed - b.initial_seed
      );
    for (const second of candidates) {
      const next = rest.filter((candidate) => candidate.id !== second.id);
      const result = search(next, [...pairs, [first, second]]);
      if (result) return result;
    }
    return null;
  };

  return search(teams, []);
}

export function pairOlympiadRound(sourceTeams, round = 1) {
  // Deterministic Swiss approximation: official start order for round one,
  // upper/lower halves within score groups with floating thereafter. Official pairing
  // software may differ because its unpublished constraints are unavailable.
  const ranked = [...sourceTeams].sort(
    (a, b) =>
      Number(b.matchPoints || 0) - Number(a.matchPoints || 0) ||
      a.initial_seed - b.initial_seed
  );
  const byeCandidates =
    ranked.length % 2
      ? [...ranked].reverse().filter((team) => !team.hadBye && !team.defaultWin)
      : [null];
  let bye = null;
  let rawPairs = null;
  for (const candidate of byeCandidates) {
    const available = candidate
      ? ranked.filter((team) => team.id !== candidate.id)
      : ranked;
    const half = available.length / 2;
    if (round === 1) {
      const proposed = available
        .slice(0, half)
        .map((team, index) => [team, available[index + half]]);
      if (proposed.every(([first, second]) => !opponents(first).has(second.id)))
        rawPairs = proposed;
    }
    if (!rawPairs) rawPairs = findCompletePairing(available);
    if (rawPairs) {
      bye = candidate;
      break;
    }
  }
  if (!rawPairs)
    throw new Error(
      "Es konnte trotz vollständiger Prüfung aller zulässigen Umordnungen keine wiederholungsfreie Olympiade-Paarung gefunden werden."
    );
  return {
    bye,
    matches: rawPairs.map(([teamA, teamB], index) => ({
      id: `sim-${round}-${index + 1}`,
      round,
      table: index + 1,
      teamA,
      teamB,
      ...coloursFor(teamA, teamB, round, index),
    })),
  };
}

export function olympiadStandings(teams) {
  // Official tie-break data is used when supplied. Until it is available,
  // board points and initial seed are explicit deterministic fallbacks.
  return [...teams].sort(
    (a, b) =>
      Number(b.matchPoints || 0) - Number(a.matchPoints || 0) ||
      Number(b.tieBreak || 0) - Number(a.tieBreak || 0) ||
      Number(b.boardPoints || 0) - Number(a.boardPoints || 0) ||
      a.initial_seed - b.initial_seed
  );
}
